import React from 'react';
import { Box, Container, Typography, Paper, List, ListItemButton, ListItemIcon, ListItemText, Breadcrumbs, Link, Drawer, IconButton, useTheme, useMediaQuery } from '@mui/material';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Home as HomeIcon,
  PlayArrow as GettingStartedIcon,
  Dashboard as BoardIcon,
  Payments as PayoutIcon,
  AccountBalanceWallet as AccountingIcon,
  Toll as CreditsIcon,
  SportsFootball as GameIcon,
  Palette as ThemesIcon,
  Notifications as NotificationsIcon,
  Group as GroupIcon,
  QuestionAnswer as FAQIcon,
  MenuBook as GlossaryIcon,
  Menu as MenuIcon,
  NavigateNext as NavigateNextIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';

interface SupportLayoutProps {
  children: React.ReactNode;
  title: string;
  description?: string;
  breadcrumbs?: { label: string; path?: string }[];
}

const NAV_ITEMS = [
  { label: 'Support Home', path: '/support', icon: <HomeIcon /> },
  { label: 'Getting Started', path: '/support/getting-started', icon: <GettingStartedIcon /> },
  { label: 'Board Management', path: '/support/boards', icon: <BoardIcon /> },
  { label: 'Game Mechanics', path: '/support/game-mechanics', icon: <GameIcon /> },
  { label: 'Payout Options', path: '/support/payouts', icon: <PayoutIcon /> },
  { label: 'Accounting & Ledger', path: '/support/accounting', icon: <AccountingIcon /> },
  { label: 'Credits & Payments', path: '/support/credits', icon: <CreditsIcon /> },
  { label: 'Themes', path: '/support/themes', icon: <ThemesIcon /> },
  { label: 'Notifications', path: '/support/notifications', icon: <NotificationsIcon /> },
  { label: 'Participation Modes', path: '/support/participation-modes', icon: <GroupIcon /> },
  { label: 'Live Games', path: '/support/live-games', icon: <GameIcon /> },
  { label: 'Data Deletion & Privacy', path: '/support/data-deletion', icon: <SecurityIcon /> },
  { label: 'FAQ', path: '/support/faq', icon: <FAQIcon /> },
  { label: 'Glossary', path: '/support/glossary', icon: <GlossaryIcon /> },
];

export const SupportLayout: React.FC<SupportLayoutProps> = ({
  children,
  title,
  description,
  breadcrumbs = [],
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = React.useState(false);

  // Scroll to anchor when hash changes
  React.useEffect(() => {
    if (location.hash) {
      // Small delay to ensure DOM is rendered
      const timeoutId = setTimeout(() => {
        const elementId = location.hash.slice(1); // Remove the # prefix
        const element = document.getElementById(elementId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [location.hash, location.pathname]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, path: string) => {
    // Allow ctrl/cmd+click or middle-click to open in new tab
    if (e.ctrlKey || e.metaKey || e.button === 1) {
      return; // Let the browser handle it naturally
    }
    e.preventDefault();
    navigate(path);
    window.scrollTo(0, 0);
    if (isMobile) setMobileOpen(false);
  };

  const navContent = (
    <List sx={{ py: 2 }}>
      {NAV_ITEMS.map((item) => (
        <ListItemButton
          key={item.path}
          component="a"
          href={item.path}
          selected={location.pathname === item.path}
          onClick={(e: React.MouseEvent<HTMLAnchorElement>) => handleNavClick(e, item.path)}
          sx={{
            borderRadius: 1,
            mx: 1,
            mb: 0.5,
            '&.Mui-selected': {
              bgcolor: 'primary.light',
              color: 'primary.contrastText',
              '& .MuiListItemIcon-root': {
                color: 'primary.contrastText',
              },
              '&:hover': {
                bgcolor: 'primary.main',
              },
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
          <ListItemText primary={item.label} primaryTypographyProps={{ fontWeight: location.pathname === item.path ? 600 : 400 }} />
        </ListItemButton>
      ))}
    </List>
  );

  // Generate canonical URL path
  const canonicalPath = location.pathname;
  const pageTitle = title === 'Support Center' ? 'Support Center | SquareUp' : `${title} | SquareUp Support`;
  const metaDescription = description || `Learn about ${title} in the SquareUp Support Center.`;

  return (
    <Box sx={{ display: 'flex', minHeight: 'calc(100vh - 200px)' }}>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content={`https://squareitup.app${canonicalPath}`} />
        <meta property="og:site_name" content="SquareUp" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={metaDescription} />
        <link rel="canonical" href={`https://squareitup.app${canonicalPath}`} />
      </Helmet>

      {/* Mobile drawer */}
      {isMobile && (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': { width: 280, boxSizing: 'border-box' },
          }}
        >
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="h6" color="primary">Support Center</Typography>
          </Box>
          {navContent}
        </Drawer>
      )}

      {/* Desktop sidebar */}
      {!isMobile && (
        <Paper
          elevation={0}
          sx={{
            width: 260,
            flexShrink: 0,
            borderRight: 1,
            borderColor: 'divider',
            bgcolor: 'grey.50',
          }}
        >
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="h6" color="primary">Support Center</Typography>
          </Box>
          {navContent}
        </Paper>
      )}

      {/* Main content */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        <Container maxWidth="lg" sx={{ py: 3 }}>
          {/* Mobile menu button */}
          {isMobile && (
            <Box sx={{ mb: 2 }}>
              <IconButton onClick={handleDrawerToggle} edge="start">
                <MenuIcon />
              </IconButton>
            </Box>
          )}

          {/* Breadcrumbs */}
          {breadcrumbs.length > 0 && (
            <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 2 }}>
              <Link
                component={RouterLink}
                to="/support"
                color="inherit"
                underline="hover"
              >
                Support
              </Link>
              {breadcrumbs.map((crumb, index) =>
                crumb.path && index < breadcrumbs.length - 1 ? (
                  <Link
                    key={crumb.label}
                    component={RouterLink}
                    to={crumb.path}
                    color="inherit"
                    underline="hover"
                  >
                    {crumb.label}
                  </Link>
                ) : (
                  <Typography key={crumb.label} color="text.primary">
                    {crumb.label}
                  </Typography>
                )
              )}
            </Breadcrumbs>
          )}

          {/* Page header */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
              {title}
            </Typography>
            {description && (
              <Typography variant="body1" color="text.secondary">
                {description}
              </Typography>
            )}
          </Box>

          {/* Page content */}
          {children}
        </Container>
      </Box>
    </Box>
  );
};

export default SupportLayout;
